import type { ColorScheme } from "@/lib/colorSchemes";
import { type ContentAlign, contentAlignClasses } from "@/lib/shared-types";

// -----------------------------------------------------------------------------
// Banner types — shared across shell, variants, and editor panels.
// All types previously in Banner.tsx live here as the single source of truth.
// -----------------------------------------------------------------------------

export type BannerHeight = 400 | 580 | 700;
export type BannerHeadingSize =
  | "lg"
  | "2xl"
  | "2xl-fluid"
  | "banner"
  | "display";
export type BannerVariant = "hero" | "rotating-word" | "standard";
export type BannerLayout = "7/5" | "6/6" | "5/7";
export type BannerBottomCurveStyle = "none" | "curve-down";
export type BannerDecorationStyle = "none" | "circle";

/** Re-exported from shared ContentAlign for backwards compatibility. */
export type BannerContentAlign = ContentAlign;

export const BANNER_CONTENT_ALIGN_CLASSES = contentAlignClasses("lg");

export interface BottomCurveConfig {
  style: BannerBottomCurveStyle;
  color: ColorScheme;
}

export interface DecorationConfig {
  style: BannerDecorationStyle;
}

export interface BannerProps {
  variant?: BannerVariant;
  eyebrow?: string;
  title?: string;
  rotatingWords?: string[];
  description?: string;
  cta?: { label: string; href: string };
  colorScheme?: ColorScheme;
  /** Foreground image — hero variant renders it in a side column. */
  image?: { src: string; alt: string; mobileSrc?: string; mobileAlt?: string };
  /** Background image — standard variant renders it as full-bleed cover. */
  backgroundImage?: { src: string; alt: string };
  height?: BannerHeight;
  layout?: BannerLayout;
  reversed?: boolean;
  accentWordColor?: string;
  headingSize?: BannerHeadingSize;
  /** Content alignment per breakpoint — "{mobile}-{desktop}". Defaults to "center-left". */
  contentAlign?: BannerContentAlign;
  bottomCurve?: BottomCurveConfig;
  decoration?: DecorationConfig;
}

// -----------------------------------------------------------------------------
// Shared constants
// -----------------------------------------------------------------------------

export const BANNER_HEIGHT_REM: Record<BannerHeight, string> = {
  400: "lg:h-[40rem]",
  580: "lg:h-[58rem]",
  700: "lg:h-[70rem]",
};

export const HEADING_SIZE_CLASS: Record<BannerHeadingSize, string> = {
  lg: "text-heading-lg",
  "2xl": "text-heading-2xl",
  "2xl-fluid": "heading-2xl-fluid",
  banner: "heading-banner",
  display: "text-heading-display",
};

export const BANNER_LAYOUT: Record<
  BannerLayout,
  { text: string; image: string }
> = {
  "7/5": {
    text: "lg:w-[58.333%]",
    image: "lg:w-[41.667%]",
  },
  "6/6": {
    text: "lg:w-1/2",
    image: "lg:w-1/2",
  },
  "5/7": {
    text: "lg:w-[41.667%]",
    image: "lg:w-[58.333%]",
  },
};

/** Basis-based layout — used by StandardBanner (lg breakpoint). */
export const STANDARD_BANNER_LAYOUT: Record<
  BannerLayout,
  { text: string; image: string }
> = {
  "7/5": {
    text: "lg:basis-7/12",
    image: "lg:basis-5/12",
  },
  "6/6": {
    text: "md:basis-1/2",
    image: "md:basis-1/2",
  },
  "5/7": {
    text: "lg:basis-5/12",
    image: "lg:basis-7/12",
  },
};

/** Z-index layering — root div uses `isolation: isolate`. */
export const Z = {
  background: 0,
  decoration: 10,
  image: 20,
  curve: 30,
  text: 40,
} as const;

/** Minimal allowlist for inline-style colour values. */
export const CSS_COLOR_RE =
  /^(#[0-9a-f]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)|var\(--[\w-]+\)|[a-z]+)$/i;

export function safeColor(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  return CSS_COLOR_RE.test(value.trim()) ? value.trim() : fallback;
}
